//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  AddTrackedHashtagsRequest,
  IInstagramDailyHistoricalMetrics,
  IInstagramHashtagAutocompleteRequest,
  IInstagramHashtagHistoricalMetricsRequest,
  IInstagramHashtagMediaResponse,
  IInstagramHashtagRecentContentRequest,
  IInstagramHashtagRelatedHashtagsRequest,
  IInstagramHashtagRelatedHashtagsResponse,
  IInstagramHashtagTopContentRequest,
  IInstagramHashtagTrackedDateResponse,
  IInstagramHashtagTrackedHashtagsResponse,
} from '@apis/instagram-hashtag/instagramHashtagApiModel';
import { deserializeMediaItem } from '@apis/media-library/mediaLibraryApiUtils';
import { IInstagramAutocompleteResponse } from '@instagram-account/models/IInstagramAutocompleteResponses';
import { IInstagramSearchRequestBody } from '@instagram-account/models/IInstagramSearchRequestBody';
import { IInstagramPost } from '@models/IInstagramMediaItem';
import InstagramHashtag from '@models/InstagramHashtag';
import { SourceType } from '@models/SourceType';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

function hashtagFetch(
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> {
  return serializedGatewayFetch(
    'instagram_dashboard_gateway',
    `/api/v1/${endpoint}`,
    init,
  );
}

/**
 *
 * Gets the daily metrics for the given hashtag within a given timeframe
 *
 * /api/v1/historical-metrics
 *
 * @returns A list of daily historical metrics
 */
export async function getDailySourceMetrics(
  req: IInstagramHashtagHistoricalMetricsRequest,
): Promise<IInstagramDailyHistoricalMetrics[]> {
  const params = makeParams({
    sourceType: SourceType.InstagramHashtag,
    ...req,
  });
  const res = await hashtagFetch(`historical-metrics?${params}`);
  return res.json();
}

/**
 *
 * Gets the top instagram posts for the given hashtag within a given timeframe
 *
 * /api/v1/top-content
 *
 * @returns A list of instagram post
 */
export async function getTopContent(
  req: IInstagramHashtagTopContentRequest,
): Promise<IInstagramPost[]> {
  const params = makeParams({
    sourceType: SourceType.InstagramHashtag,
    ...req,
  });
  const res = await hashtagFetch(`top-content?${params}`);
  return res.json();
}

/**
 * Returns the daily metrics for the given hashtag within a given timeframe as a CSV
 *
 * /api/v1/historical-metrics.csv
 */
export async function getDailyMetricsCsv(
  req: IInstagramHashtagHistoricalMetricsRequest,
): Promise<string> {
  const params = makeParams({
    sourceType: SourceType.InstagramHashtag,
    ...req,
  });
  const res = await hashtagFetch(`historical-metrics.csv?${params}`);
  return res.text();
}

/**
 * Returns the top instagram posts for the given hashtag within a given timeframe as a CSV
 *
 * /api/v1/top-content.csv
 */
export async function getTopContentCsv(
  req: IInstagramHashtagTopContentRequest,
): Promise<string> {
  const params = makeParams({
    sourceType: SourceType.InstagramHashtag,
    ...req,
  });
  const res = await hashtagFetch(`top-content.csv?${params}`);
  return res.text();
}

/**
 *
 * Gets the recent instgram posts for the given hashtag within a given timeframe
 *
 * /api/v1/hashtags/media
 *
 * @returns A set of data which includes a list of instagram post
 */
export async function getRecentContent(
  req: IInstagramHashtagRecentContentRequest,
  body?: IInstagramSearchRequestBody,
): Promise<IInstagramHashtagMediaResponse> {
  const params = makeParams(req);
  const res = await hashtagFetch(`hashtags/media?${params}`, {
    body: JSON.stringify(body),
    method: 'POST',
  });
  return res.json<IInstagramHashtagMediaResponse>((res) => ({
    data: {
      ...res.data,
      media: res.data.media.map(deserializeMediaItem),
    },
  }));
}

/**
 *
 * Gets all currently tracked hashtags
 *
 * /api/v1/hashtags
 *
 * @returns A list of currently tracked hashtags
 */
export async function getTrackedHashtags(): Promise<IInstagramHashtagTrackedHashtagsResponse> {
  const res = await hashtagFetch('hashtags');
  return res.json();
}

/**
 *
 * Gets the initial tracked date for the given hashtag
 *
 * /api/v1/hashtags/details
 *
 * @returns A date in milliseconds
 */
export async function getInitialTrackedDate(
  hashtag: InstagramHashtag,
): Promise<IInstagramHashtagTrackedDateResponse> {
  const params = makeParams({ hashtag });
  const res = await hashtagFetch(`hashtags/details?${params}`);
  return res.json();
}

/**
 * Returns a zip file containing a summary and content for a tracked hashtag
 *
 * /api/v1/hashtags/export.zip
 */
export async function getRecentContentZip(
  req: IInstagramHashtagRecentContentRequest,
  body?: IInstagramSearchRequestBody,
): Promise<Blob> {
  const params = makeParams(req);
  const res = await hashtagFetch(`hashtags/export.zip?${params}`, {
    body: JSON.stringify(body),
    method: 'POST',
  });
  return res.blob();
}

/**
 * Adds a tracked hashtag for the current client
 *
 * /api/v1/hashtags/add
 *
 */
export async function addTrackedHashtags(
  { businessAccountId, hashtags }: AddTrackedHashtagsRequest,
  init?: RequestInit,
): Promise<IInstagramHashtagTrackedHashtagsResponse> {
  const params = makeParams({ businessAccountId });
  const res = await hashtagFetch(`hashtags/add?${params}`, {
    method: 'POST',
    body: JSON.stringify({
      hashtags,
    }),
    ...init,
  });
  const json = await res.json<{
    data: IInstagramHashtagTrackedHashtagsResponse;
  }>();
  return json.data;
}

/**
 * Bulk remove the provided hashtags as tracked sources for the client
 *
 * /api/v1/hashtags/remove
 *
 */
export async function removeTrackedHashtags(
  { businessAccountId, hashtags }: AddTrackedHashtagsRequest,
  init?: RequestInit,
): Promise<void> {
  const params = makeParams({ businessAccountId });
  await hashtagFetch(`hashtags/remove?${params}`, {
    method: 'POST',
    body: JSON.stringify({
      hashtags,
    }),
    ...init,
  });
}

/**
 *
 * Get autocomplete suggestions for a tracked hashtag
 *
 * /api/v1/hashtags/autocomplete
 *
 * @returns A list of matching autocomplete results
 */
export async function getHashtagAutocompleteSuggestions(
  req: IInstagramHashtagAutocompleteRequest,
  body?: IInstagramSearchRequestBody,
): Promise<IInstagramAutocompleteResponse> {
  const params = makeParams(req);
  const res = await hashtagFetch(`hashtags/autocomplete?${params}`, {
    body: JSON.stringify(body),
    method: 'POST',
  });
  return res.json();
}

/**
 *
 * Get related hashtags for a tracked hashtag
 *
 * /api/v1/hashtags/related
 *
 */
export async function getRelatedHashtagsForTrackedHashtag(
  req: IInstagramHashtagRelatedHashtagsRequest,
  body?: IInstagramSearchRequestBody,
): Promise<IInstagramHashtagRelatedHashtagsResponse> {
  const params = makeParams(req);
  const res = await hashtagFetch(`hashtags/related?${params}`, {
    body: JSON.stringify(body),
    method: 'POST',
  });
  return res.json();
}
